import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import UtilService from "../../services/UtilService";

/** Grid column field -> API sort property (list endpoint uses updatedAt, not updated). */
const USER_LIST_SORT_FIELD = {
    updated: "updatedAt",
};

const AccountService = {
    requestUser: async (
        keyword,
        _page,
        pageSize,
        sort,
        setRows,
        setRowCount,
        testAccountFilter = "exclude",
        excludeStaffUser = false,
        excludeAppleStaff = false,
        appCode = "",
    ) => {
        let params = {
            page: _page ?? 0,
            pageSize: pageSize,
            sort: sort
                .map((s) => `${USER_LIST_SORT_FIELD[s.field] ?? s.field}:${s.sort}`)
                .join(","),
            keyword: keyword,
        };
        if (testAccountFilter === "exclude") {
            params.testAccount = false;
        } else if (testAccountFilter === "include") {
            params.testAccount = true;
        }
        if (excludeStaffUser) {
            params.staff = false;
        }
        if (excludeAppleStaff) {
            params.excludeApplePrivateRelay = true;
        }
        if (appCode && String(appCode).trim()) {
            params.appCode = String(appCode).trim();
        }

        const result = await ApiService.getData("/v1/user", params);

        const mapped = result.content.map((row) => {
            const updatedTs = row.updatedAt ?? row.updated;
            const updatedDisplay =
                updatedTs != null && updatedTs !== 0
                    ? UtilService.formatMDY(updatedTs) || "N/A"
                    : "N/A";
            const createdTs = row.createdAt;
            const createdDisplay =
                createdTs != null && createdTs !== 0
                    ? UtilService.formatMDY(createdTs) || "N/A"
                    : "N/A";
            const lastLoggedInTs = row.lastLoggedInAt ?? row.last_logged_in_at;
            const lastLoggedInDisplay =
                lastLoggedInTs != null && lastLoggedInTs !== 0
                    ? UtilService.formatDate(lastLoggedInTs) || "N/A"
                    : "";
            const notificationsByApp =
                row.details && typeof row.details.notificationsByApp === "object"
                    ? row.details.notificationsByApp
                    : null;

            return {
                id: row.id,
                name: [row.firstName, row.lastName]
                    .map((part) => (part == null ? "" : String(part).trim()))
                    .filter(Boolean)
                    .join(" "),
                email: row.email,
                emailVerified: row.emailVerified,
                enabled: row.enabled,
                updated: updatedDisplay,
                createdAt: createdDisplay,
                lastLoggedInAt: lastLoggedInDisplay,
                userRole: row.userRole,
                userRoles: row.userRoles,
                payPlans: row.payPlans ?? row.pay_plans ?? null,
                signupSource: row.signupSource ?? row.signup_source ?? null,
                stripeLivemode: row.stripeLivemode,
                playApps: [],
                notificationsByApp,
                pushTokens: [],
            };
        });

        setRowCount(result.page?.totalElements ?? 0);
        setRows(mapped);

        const cloudUserIds = mapped.map((r) => r.id).filter(Boolean);
        if (cloudUserIds.length === 0) {
            return;
        }
        try {
            const [playsResult, tokensResult] = await Promise.allSettled([
                ApiService.postData("/v1/stats/user/plays-by-app/batch", {
                    cloudUserIds,
                }),
                ApiService.postData("/v1/push/device-tokens/batch", {
                    cloudUserIds,
                }),
            ]);
            if (playsResult.status === "rejected") {
                Logger.error("Error fetching plays-by-app batch:", playsResult.reason);
            }
            if (tokensResult.status === "rejected") {
                Logger.error("Error fetching push tokens batch:", tokensResult.reason);
            }
            const byUserId = playsResult.status === "fulfilled" ? (playsResult.value?.byUserId ?? {}) : {};
            const tokensByUserId =
                tokensResult.status === "fulfilled" ? (tokensResult.value?.byUserId ?? {}) : {};
            setRows(
                mapped.map((row) => {
                    const stats = byUserId[row.id];
                    const apps = Array.isArray(stats?.apps) ? stats.apps : [];
                    const playApps = apps
                        .filter((a) => Number(a?.countPlay) > 0)
                        .map((a) => ({
                            appCode: a.appCode,
                            label: a.label || a.appCode,
                            countPlay: Number(a.countPlay) || 0,
                            countFinished: Number(a.countFinished) || 0,
                        }));
                    const pushTokens = Array.isArray(tokensByUserId[row.id]) ? tokensByUserId[row.id] : [];
                    return { ...row, playApps, pushTokens };
                }),
            );
        } catch (error) {
            Logger.error("Error fetching account list extras:", error);
        }
    },

    /**
     * GET /v1/user/signup-timeline — signup order (n, email, createdAt) for the account chart.
     */
    signupTimeline: async ({
        keyword = "",
        testAccount,
        staff,
        excludeApplePrivateRelay = false,
        appCode = "",
    } = {}) => {
        try {
            const params = {};
            if (keyword) params.keyword = keyword;
            if (testAccount === true) params.testAccount = "true";
            if (testAccount === false) params.testAccount = "false";
            if (staff === false) params.staff = "false";
            if (excludeApplePrivateRelay) params.excludeApplePrivateRelay = "true";
            if (appCode && String(appCode).trim()) params.appCode = String(appCode).trim();
            return await ApiService.getData("/v1/user/signup-timeline", params);
        } catch (error) {
            Logger.error("Error fetching user signup timeline:", error);
            throw error;
        }
    },

    /**
     * GET /v1/user/counts-by-app — signed-in user counts per web/app, guest play sessions, and sign-up source.
     */
    countsByApp: async ({
        keyword = "",
        testAccount,
        staff,
        excludeApplePrivateRelay = false,
        period = "all",
    } = {}) => {
        try {
            const params = {};
            if (keyword) params.keyword = keyword;
            if (testAccount === true) params.testAccount = "true";
            if (testAccount === false) params.testAccount = "false";
            if (staff === false) params.staff = "false";
            if (excludeApplePrivateRelay) params.excludeApplePrivateRelay = "true";
            if (period && period !== "all") params.period = period;
            return await ApiService.getData("/v1/user/counts-by-app", params);
        } catch (error) {
            Logger.error("Error fetching user counts by app:", error);
            throw error;
        }
    },

    /**
     * GET /v1/user/notification-counts-by-platform — on/off user counts per iOS, Android, Web.
     */
    notificationCountsByPlatform: async ({
        keyword = "",
        testAccount,
        staff,
        excludeApplePrivateRelay = false,
    } = {}) => {
        try {
            const params = {};
            if (keyword) params.keyword = keyword;
            if (testAccount === true) params.testAccount = "true";
            if (testAccount === false) params.testAccount = "false";
            if (staff === false) params.staff = "false";
            if (excludeApplePrivateRelay) params.excludeApplePrivateRelay = "true";
            return await ApiService.getData("/v1/user/notification-counts-by-platform", params);
        } catch (error) {
            Logger.error("Error fetching notification counts by platform:", error);
            throw error;
        }
    },

    /**
     * GET /v1/user/notification-enabled-on-aws — users with an AWS SNS endpoint.
     */
    notificationEnabledOnAws: async ({
        keyword = "",
        testAccount,
        staff,
        excludeApplePrivateRelay = false,
    } = {}) => {
        try {
            const params = {};
            if (keyword) params.keyword = keyword;
            if (testAccount === true) params.testAccount = "true";
            if (testAccount === false) params.testAccount = "false";
            if (staff === false) params.staff = "false";
            if (excludeApplePrivateRelay) params.excludeApplePrivateRelay = "true";
            return await ApiService.getData("/v1/user/notification-enabled-on-aws", params);
        } catch (error) {
            Logger.error("Error fetching users enabled on AWS SNS:", error);
            throw error;
        }
    },

    getUserDetails: async (userId) => {
        try {
            const response = await ApiService.getData(`/v1/user/${userId}`);
            Logger.log("User details response:", response);
            
            // Handle different possible response structures
            // API might return the user object directly, or wrapped in 'data' or 'content'
            if (response && typeof response === 'object') {
                // Check if response has an error code
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to fetch user details");
                }
                
                // Return the response directly if it looks like a user object
                // Or unwrap if it's nested
                return response.data || response.content || response;
            }
            
            return response;
        } catch (error) {
            Logger.error("Error fetching user details:", error);
            throw error;
        }
    },

    /**
     * GET /v1/assistant/credits/history — paged ledger (newest first). Response may be `{ encrypted }`;
     * ApiService decrypts like chat history.
     * @param {{ page?: number, pageSize?: number }} opts - page 0-based; pageSize clamped 1–100
     */
    getAssistantCreditsHistory: async (opts = {}) => {
        const page = Math.max(0, Number(opts.page) || 0);
        const rawSize = Number(opts.pageSize);
        const pageSize = Math.min(100, Math.max(1, Number.isFinite(rawSize) ? rawSize : 20));
        try {
            const response = await ApiService.getData("/v1/assistant/credits/history", {
                page,
                pageSize,
            });
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to fetch assistant credits history");
                }
            }
            return response;
        } catch (error) {
            Logger.error("Error fetching assistant credits history:", error);
            throw error;
        }
    },

    getUserSubscriptions: async (userId, page = 0, pageSize = 10, sort = []) => {
        try {
            const params = {
                page: page,
                pageSize: pageSize
            };
            
            // Add sort parameter if provided
            if (sort && sort.length > 0) {
                params.sort = sort.map((s) => `${s.field}:${s.sort}`).join(",");
            }
            
            const response = await ApiService.getData(`/v1/sub/user/${userId}`, params);
            Logger.log("User subscriptions response:", response);
            
            // Handle different possible response structures
            if (response && typeof response === 'object') {
                // Check if response has an error code
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to fetch user subscriptions");
                }
                
                // If response is paginated (has content, totalElements, etc.)
                if (response.content !== undefined) {
                    const p = response.page;
                    return {
                        content: response.content || [],
                        totalElements: p?.totalElements ?? 0,
                        totalPages: p?.totalPages ?? 0,
                        number: p?.number ?? page,
                        size: p?.size ?? response.size ?? pageSize
                    };
                }
                
                // Subscriptions might be an array directly, or wrapped in 'data'
                if (Array.isArray(response)) {
                    return {
                        content: response,
                        totalElements: response.length,
                        totalPages: 1,
                        number: page,
                        size: pageSize
                    };
                }
                
                // If wrapped in 'data'
                if (response.data) {
                    const p = response.page;
                    return {
                        content: Array.isArray(response.data) ? response.data : [],
                        totalElements: p?.totalElements ?? (Array.isArray(response.data) ? response.data.length : 0),
                        totalPages: p?.totalPages ?? 1,
                        number: p?.number ?? page,
                        size: p?.size ?? response.size ?? pageSize
                    };
                }
            }
            
            // Return empty paginated response if response is null/undefined
            return {
                content: [],
                totalElements: 0,
                totalPages: 0,
                number: page,
                size: pageSize
            };
        } catch (error) {
            Logger.error("Error fetching user subscriptions:", error);
            throw error;
        }
    },


    resetPlayStatistics: async (userId) => {
        try {
            const response = await ApiService.deleteData(`/v1/puzzle/grid-play/${userId}/plays`);
            Logger.log("Reset play statistics response:", response);
            
            // Handle different possible response structures
            if (response && typeof response === 'object') {
                // Check if response has an error code
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to reset play statistics");
                }
                
                return response;
            }
            
            return response;
        } catch (error) {
            Logger.error("Error resetting play statistics:", error);
            throw error;
        }
    },

    forceEndSubscription: async (subscriptionId) => {
        try {
            const response = await ApiService.putData(`/v1/sub/${subscriptionId}/force-end`);
            Logger.log("Force end subscription response:", response);
            
            if (response && typeof response === 'object') {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to force end subscription");
                }
                
                return response;
            }
            
            return response;
        } catch (error) {
            Logger.error("Error force ending subscription:", error);
            throw error;
        }
    },

    /**
     * Admin: sync subscription status from Stripe for a customer.
     * GET /v1/stripe/status-check?customerId=cus_...
     * Success: plain JSON (not CustomResponseRo). Failure: { code: "FAILURE", message } with HTTP 200.
     */
    stripeStatusCheck: async (customerId) => {
        if (!customerId || String(customerId).trim() === "") {
            throw new Error("customerId is required");
        }
        try {
            const response = await ApiService.getData("/v1/stripe/status-check", {
                customerId: String(customerId).trim(),
            });
            Logger.log("Stripe status-check response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Stripe status check failed");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error in Stripe status-check:", error);
            throw error;
        }
    },

    /**
     * Admin: cancel Stripe subscriptions for a customer and update local DB.
     * POST /v1/stripe/cancel?customerId=cus_...
     */
    stripeCancelForCustomer: async (customerId) => {
        if (!customerId || String(customerId).trim() === "") {
            throw new Error("customerId is required");
        }
        const q = encodeURIComponent(String(customerId).trim());
        try {
            const response = await ApiService.postData(`/v1/stripe/cancel?customerId=${q}`, {});
            Logger.log("Stripe cancel response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Stripe cancel failed");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error in Stripe cancel:", error);
            throw error;
        }
    },

    /**
     * Admin-only. Stripe checkout sessions and jigsaw pack grants for this account.
     * GET /v1/user/{cloudUserId}/web-purchases
     */
    getWebPurchases: async (cloudUserId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.getData(`/v1/user/${cloudUserId}/web-purchases`);
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to load web purchases");
                }
            }
            return response;
        } catch (error) {
            Logger.error("Error fetching web purchases:", error);
            throw error;
        }
    },

    /**
     * Admin-only: mark user as test account or not.
     * PUT /v1/user/{cloudUserId}/test-account
     */
    /**
     * Admin-only, test accounts only. Grants Tutor Standard and 50 extra PDF credits.
     * POST /v1/user/{cloudUserId}/tutor-test-credit
     */
    grantTutorTestCredit: async (cloudUserId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.postData(`/v1/user/${cloudUserId}/tutor-test-credit`, {});
            Logger.log("Grant tutor test credit response:", response);
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to add test credit");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error granting tutor test credit:", error);
            throw error;
        }
    },

    /**
     * Admin-only, test accounts only. Removes one test grant of 50 extra PDF credits.
     * POST /v1/user/{cloudUserId}/tutor-test-credit/remove
     */
    removeTutorTestCredit: async (cloudUserId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.postData(`/v1/user/${cloudUserId}/tutor-test-credit/remove`, {});
            Logger.log("Remove tutor test credit response:", response);
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to remove test credit");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error removing tutor test credit:", error);
            throw error;
        }
    },

    updateTestAccount: async (cloudUserId, testAccount) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.putData(`/v1/user/${cloudUserId}/test-account`, {
                testAccount: Boolean(testAccount),
            });
            Logger.log("Update test account response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to update test account flag");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error updating test account:", error);
            throw error;
        }
    },

    /**
     * Admin-only: reset a test account's plays, pay plans, coins, and related wallets.
     * The account stays enabled and loginable.
     * POST /v1/user/{cloudUserId}/reset-test
     */
    resetTestAccount: async (cloudUserId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.postData(`/v1/user/${cloudUserId}/reset-test`, {});
            Logger.log("Reset test account response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to reset test account");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error resetting test account:", error);
            throw error;
        }
    },

    /**
     * Admin-only: mark user as staff or not. Staff are excluded from admin play analytics.
     * PUT /v1/user/{cloudUserId}/staff
     */
    updateStaff: async (cloudUserId, staff) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.putData(`/v1/user/${cloudUserId}/staff`, {
                staff: Boolean(staff),
            });
            Logger.log("Update staff response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to update staff flag");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error updating staff flag:", error);
            throw error;
        }
    },

    /**
     * Admin-only: mark user as developer or not (play-settings Developer tools on device).
     * PUT /v1/user/{cloudUserId}/developer
     */
    updateDeveloper: async (cloudUserId, developer) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.putData(`/v1/user/${cloudUserId}/developer`, {
                developer: Boolean(developer),
            });
            Logger.log("Update developer response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to update developer flag");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error updating developer flag:", error);
            throw error;
        }
    },

    updateUserRole: async (cloudUserId, roleOrRoles) => {
        try {
            const roles = Array.isArray(roleOrRoles)
                ? roleOrRoles.filter(Boolean)
                : [roleOrRoles].filter(Boolean);
            const response = await ApiService.putData(`/v1/user/${cloudUserId}/role`, {
                role: roles[0],
                roles,
            });
            Logger.log("Update user role response:", response);
            
            if (response && typeof response === 'object') {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to update user role");
                }
                
                return response;
            }
            
            return response;
        } catch (error) {
            Logger.error("Error updating user role:", error);
            throw error;
        }
    },

    /**
     * Admin-only: set or clear internal memo for a user.
     * PUT /v1/user/{cloudUserId}/memo — empty/whitespace clears (memo: null).
     */
    updateUserMemo: async (cloudUserId, memoText) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const trimmed = memoText?.trim() ?? "";
            const body = trimmed === "" ? { memo: null } : { memo: trimmed };
            const response = await ApiService.putData(`/v1/user/${cloudUserId}/memo`, body);
            Logger.log("Update user memo response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to update memo");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error updating user memo:", error);
            throw error;
        }
    },

    /**
     * Admin/testing: list a user's registered push device tokens.
     * GET /v1/push/device-tokens?cloudUserId=... — returns { pushEnabled, content: [...] }.
     */
    listPushDeviceTokens: async (cloudUserId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.getData("/v1/push/device-tokens", { cloudUserId });
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to load device tokens");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error loading device tokens:", error);
            throw error;
        }
    },

    /**
     * Admin/testing: send a push notification to a user's registered devices.
     * POST /v1/push/test — appCode blank targets all of the user's tokens.
     */
    sendTestNotification: async (cloudUserId, { appCode = "", deviceTokenIds = [], title = "", body = "" } = {}) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        try {
            const payload = { cloudUserId };
            if (appCode && appCode.trim() !== "") payload.appCode = appCode.trim();
            if (Array.isArray(deviceTokenIds) && deviceTokenIds.length > 0) {
                payload.deviceTokenIds = deviceTokenIds;
            }
            if (title && title.trim() !== "") payload.title = title.trim();
            if (body && body.trim() !== "") payload.body = body.trim();
            const response = await ApiService.postData("/v1/push/test", payload);
            Logger.log("Send test notification response:", response);

            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to send test notification");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error sending test notification:", error);
            throw error;
        }
    },

    /**
     * Admin/testing: delete one registered push device token row for a user.
     * DELETE /v1/push/device-tokens/{deviceTokenId}?cloudUserId=...
     */
    deletePushDeviceToken: async (cloudUserId, deviceTokenId) => {
        if (!cloudUserId) {
            throw new Error("User ID is required");
        }
        if (!deviceTokenId) {
            throw new Error("Device token ID is required");
        }
        try {
            const response = await ApiService.deleteData(
                `/v1/push/device-tokens/${encodeURIComponent(deviceTokenId)}?cloudUserId=${encodeURIComponent(cloudUserId)}`,
            );
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to delete device token");
                }
                return response;
            }
            return response;
        } catch (error) {
            Logger.error("Error deleting device token:", error);
            throw error;
        }
    },

    /**
     * Admin-only: irreversibly deletes the user and associated data (same pipeline as self-service data deletion).
     * DELETE /v1/user/{id}/remove
     */
    removeUser: async (userId) => {
        if (!userId) {
            throw new Error("User ID is required");
        }
        try {
            const response = await ApiService.deleteData(`/v1/user/${userId}/remove`);
            if (response && typeof response === "object") {
                if (response.code === "FAILURE" || response.error) {
                    throw new Error(response.message || response.error || "Failed to remove user");
                }
            }
            return response;
        } catch (error) {
            Logger.error("Error removing user:", error);
            throw error;
        }
    },
};

export default AccountService;
